import { createSlice } from '@reduxjs/toolkit';
import { HelpdeskSettingsState } from './types';
import {
  fetchHelpdeskSettings,
  updateHelpdeskSettings,
  fetchRoutingRules,
  createRoutingRule,
  updateRoutingRule,
  deleteRoutingRule,
  fetchSLAPolicies,
  createSLAPolicy,
  updateSLAPolicy,
  deleteSLAPolicy,
  fetchSchedules,
  updateSchedule,
  createSchedule,
  deleteSchedule,
  deleteOperatorSchedules,
  fetchTicketTypes,
  createTicketType,
  updateTicketType,
  deleteTicketType,
  fetchScheduleExceptions,
  createScheduleException,
  deleteScheduleException,
  fetchTicketStatuses,
  createTicketStatus,
  updateTicketStatus,
  deleteTicketStatus,
} from './thunks';

const initialState: HelpdeskSettingsState = {
  settings: null,
  routingRules: [],
  slaPolicies: [],
  schedules: [],
  ticketTypes: [],
  ticketStatuses: [],
  scheduleExceptions: [],
  status: 'idle',
  rulesStatus: 'idle',
  slaStatus: 'idle',
  schedulesStatus: 'idle',
  ticketTypesStatus: 'idle',
  ticketStatusesStatus: 'idle',
  scheduleExceptionsStatus: 'idle',
  error: null,
};

const helpdeskSettingsSlice = createSlice({
  name: 'helpdeskSettings',
  initialState,
  reducers: {
    clearHdSettingsError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // --- Settings singleton ---
    builder
      .addCase(fetchHelpdeskSettings.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchHelpdeskSettings.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.settings = action.payload;
      })
      .addCase(fetchHelpdeskSettings.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload ?? 'Ошибка';
      });

    builder.addCase(updateHelpdeskSettings.fulfilled, (state, action) => {
      state.settings = action.payload;
    });

    // --- Routing Rules ---
    builder
      .addCase(fetchRoutingRules.pending, (state) => {
        state.rulesStatus = 'loading';
      })
      .addCase(fetchRoutingRules.fulfilled, (state, action) => {
        state.rulesStatus = 'succeeded';
        state.routingRules = action.payload;
      })
      .addCase(fetchRoutingRules.rejected, (state, action) => {
        state.rulesStatus = 'failed';
        state.error = action.payload ?? 'Ошибка';
      });

    builder.addCase(createRoutingRule.fulfilled, (state, action) => {
      state.routingRules.push(action.payload);
    });
    builder.addCase(updateRoutingRule.fulfilled, (state, action) => {
      const idx = state.routingRules.findIndex((r) => r.id === action.payload.id);
      if (idx !== -1) state.routingRules[idx] = action.payload;
    });
    builder.addCase(deleteRoutingRule.fulfilled, (state, action) => {
      state.routingRules = state.routingRules.filter((r) => r.id !== action.payload);
    });

    // --- SLA Policies ---
    builder
      .addCase(fetchSLAPolicies.pending, (state) => {
        state.slaStatus = 'loading';
      })
      .addCase(fetchSLAPolicies.fulfilled, (state, action) => {
        state.slaStatus = 'succeeded';
        state.slaPolicies = action.payload;
      })
      .addCase(fetchSLAPolicies.rejected, (state, action) => {
        state.slaStatus = 'failed';
        state.error = action.payload ?? 'Ошибка';
      });

    builder.addCase(createSLAPolicy.fulfilled, (state, action) => {
      state.slaPolicies.push(action.payload);
    });
    builder.addCase(updateSLAPolicy.fulfilled, (state, action) => {
      const idx = state.slaPolicies.findIndex((p) => p.id === action.payload.id);
      if (idx !== -1) state.slaPolicies[idx] = action.payload;
    });
    builder.addCase(deleteSLAPolicy.fulfilled, (state, action) => {
      state.slaPolicies = state.slaPolicies.filter((p) => p.id !== action.payload);
    });

    // --- Schedules ---
    builder
      .addCase(fetchSchedules.pending, (state) => {
        state.schedulesStatus = 'loading';
      })
      .addCase(fetchSchedules.fulfilled, (state, action) => {
        state.schedulesStatus = 'succeeded';
        state.schedules = action.payload;
      })
      .addCase(fetchSchedules.rejected, (state, action) => {
        state.schedulesStatus = 'failed';
        state.error = action.payload ?? 'Ошибка';
      });

    builder.addCase(updateSchedule.fulfilled, (state, action) => {
      const idx = state.schedules.findIndex((s) => s.id === action.payload.id);
      if (idx !== -1) state.schedules[idx] = action.payload;
    });
    builder.addCase(createSchedule.fulfilled, (state, action) => {
      state.schedules.push(action.payload);
    });
    builder.addCase(deleteSchedule.fulfilled, (state, action) => {
      state.schedules = state.schedules.filter((s) => s.id !== action.payload);
    });
    builder.addCase(deleteOperatorSchedules.fulfilled, (state, action) => {
      const removed = new Set(action.payload);
      state.schedules = state.schedules.filter((s) => !removed.has(s.id));
    });
    builder.addCase(deleteOperatorSchedules.rejected, (state, action) => {
      state.error = action.payload ?? 'Ошибка';
    });

    // --- Schedule Exceptions ---
    builder
      .addCase(fetchScheduleExceptions.pending, (state) => {
        state.scheduleExceptionsStatus = 'loading';
      })
      .addCase(fetchScheduleExceptions.fulfilled, (state, action) => {
        state.scheduleExceptionsStatus = 'succeeded';
        state.scheduleExceptions = action.payload;
      })
      .addCase(fetchScheduleExceptions.rejected, (state, action) => {
        state.scheduleExceptionsStatus = 'failed';
        state.error = action.payload ?? 'Ошибка';
      });

    builder.addCase(createScheduleException.fulfilled, (state, action) => {
      state.scheduleExceptions.push(action.payload);
      state.scheduleExceptions.sort((a, b) => a.date.localeCompare(b.date));
    });
    builder.addCase(createScheduleException.rejected, (state, action) => {
      state.error = action.payload ?? 'Ошибка';
    });
    builder.addCase(deleteScheduleException.fulfilled, (state, action) => {
      state.scheduleExceptions = state.scheduleExceptions.filter(
        (e) => e.id !== action.payload,
      );
    });

    // --- Ticket Statuses ---
    builder
      .addCase(fetchTicketStatuses.pending, (state) => {
        state.ticketStatusesStatus = 'loading';
      })
      .addCase(fetchTicketStatuses.fulfilled, (state, action) => {
        state.ticketStatusesStatus = 'succeeded';
        state.ticketStatuses = action.payload;
      })
      .addCase(fetchTicketStatuses.rejected, (state, action) => {
        state.ticketStatusesStatus = 'failed';
        state.error = action.payload ?? 'Ошибка';
      });

    builder.addCase(createTicketStatus.fulfilled, (state, action) => {
      state.ticketStatuses.push(action.payload);
      state.ticketStatuses.sort((a, b) => a.sort_order - b.sort_order);
    });
    builder.addCase(createTicketStatus.rejected, (state, action) => {
      state.error = action.payload ?? 'Ошибка';
    });
    builder.addCase(updateTicketStatus.fulfilled, (state, action) => {
      const idx = state.ticketStatuses.findIndex((s) => s.id === action.payload.id);
      if (idx !== -1) state.ticketStatuses[idx] = action.payload;
    });
    builder.addCase(updateTicketStatus.rejected, (state, action) => {
      state.error = action.payload ?? 'Ошибка';
    });
    builder.addCase(deleteTicketStatus.fulfilled, (state, action) => {
      state.ticketStatuses = state.ticketStatuses.filter(
        (s) => s.id !== action.payload,
      );
    });
    builder.addCase(deleteTicketStatus.rejected, (state, action) => {
      state.error = action.payload ?? 'Ошибка';
    });

    // --- Ticket Types ---
    builder
      .addCase(fetchTicketTypes.pending, (state) => {
        state.ticketTypesStatus = 'loading';
      })
      .addCase(fetchTicketTypes.fulfilled, (state, action) => {
        state.ticketTypesStatus = 'succeeded';
        state.ticketTypes = action.payload;
      })
      .addCase(fetchTicketTypes.rejected, (state, action) => {
        state.ticketTypesStatus = 'failed';
        state.error = action.payload ?? 'Ошибка';
      });

    builder.addCase(createTicketType.fulfilled, (state, action) => {
      state.ticketTypes.push(action.payload);
    });
    builder.addCase(updateTicketType.fulfilled, (state, action) => {
      const idx = state.ticketTypes.findIndex((t) => t.id === action.payload.id);
      if (idx !== -1) state.ticketTypes[idx] = action.payload;
    });
    builder.addCase(deleteTicketType.fulfilled, (state, action) => {
      state.ticketTypes = state.ticketTypes.filter((t) => t.id !== action.payload);
    });
    builder.addCase(deleteTicketType.rejected, (state, action) => {
      state.error = action.payload ?? 'Ошибка';
    });
  },
});

export const { clearHdSettingsError } = helpdeskSettingsSlice.actions;
export default helpdeskSettingsSlice.reducer;
